<template>
  <div class="mt-2 flex overflow-hidden flex-grow gap-2">
    <div class="flex-grow flex flex-col mx-auto bg-white rounded gap-2 p-2 overflow-auto">

      <div class="w-full bg-primary p-2 rounded">
        Neue Regel
      </div>

      <div class="flex space-x-2">
        <span class="bg-primary w-[200px] p-2 rounded">Title</span>
        <input class="border p-2 rounded flex-grow" v-model="ruleTitle" data-ui="name"/>
      </div>

      <div class="flex space-x-2">
        <span class="bg-primary w-[200px] p-2 rounded">Bedingung</span>
        <div class="flex flex-col flex-grow">
          <!-- Implemented the hook selection component -->
          <div class="rounded flex flex-wrap gap-2">
            <button
                v-for="hook in hooks"
                :key="hook.value"
                @click="toggleHook(hook.value)"
                class="px-3 py-2 rounded text-sm transition duration-200 ease-in-out"
                :class="ruleHook.includes(hook.value) ? 'bg-trader-blue text-white' : 'bg-primary hover:bg-trader-blue hover:text-white'"
            >
              {{ hook.label }}
            </button>
          </div>
        </div>
      </div>

      <div class="flex space-x-2">
        <span class="bg-primary w-[200px] p-2 rounded">Legende</span>
        <div class="flex flex-col flex-grow">
          <!-- Implemented the function description component -->
          <div class="bg-trader-gray p-2 rounded">
            <div class="mb-3">
              <h3 class="font-bold">getFieldValue(fieldName)</h3>
              <p class="text-sm">Holt den Wert eines bestimmten Feldes vom Produkt.</p>
            </div>
            <div class="mb-3">
              <h3 class="font-bold">setFieldValue(fieldName, value)</h3>
              <p class="text-sm">Setzt den Wert eines bestimmten Feldes im Produkt.</p>
            </div>
            <div class="mb-3">
              <h3 class="font-bold">roundToThreeDecimal(value)</h3>
              <p class="text-sm">Rundet eine Zahl auf maximal 3 Dezimalstellen, entfernt nachfolgende Nullen.</p>
            </div>
            <div class="mb-3">
              <h3 class="font-bold">toFloat(value)</h3>
              <p class="text-sm">Konvertiert einen String in eine Gleitkommazahl, berücksichtigt sowohl Punkt als auch Komma als Dezimaltrennzeichen.</p>
            </div>
            <div class="mb-3">
              <h3 class="font-bold">calcPriceWithVat(price, vatRate)</h3>
              <p class="text-sm">Berechnet den Preis inklusive Mehrwertsteuer basierend auf dem Nettopreis und dem MwSt-Satz.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="flex space-x-2">
        <span class="bg-primary w-[200px] h-[34px] p-2 rounded flex-shrink-0">Script</span>
        <div class="flex flex-grow bg-trader-gray p-2 rounded">
          <CodeEditor v-model="ruleScript" :header="false" :languages="[['js']]" class="flex-grow"
                      :width="'100'" :borderRadius="false" :font-size="'12px'" />
        </div>
      </div>

      <div class="flex gap-2">
        <button v-if="ruleId == -1"
                class="w-full rounded mt-2 p-3 bg-primary hover:bg-trader-blue hover:text-white transition duration-200 ease-in-out"
                @click="createRule" data-ui="create-EKB-button">
          Erstellen
        </button>

        <button v-if="ruleId != -1"
                class="w-full rounded mt-2 p-3 bg-primary hover:bg-trader-blue hover:text-white transition duration-200 ease-in-out"
                @click="createRule" data-ui="create-EKB-button">
          Änderungen speichern
        </button>

        <button v-if="ruleId != -1"
                class="w-full rounded mt-2 p-3 bg-primary hover:bg-trader-blue hover:text-white transition duration-200 ease-in-out"
                @click="deleteRule" data-ui="create-EKB-button">
          Regel löschen
        </button>
      </div>
    </div>
    <sidebar/>
  </div>
</template>

<script setup>
import {onMounted, ref, watch} from "vue";
import sidebar from "@/components/Administration/Sidebar.vue";
import {mutation, query} from "@/graphql";
import hljs from "highlight.js";
import CodeEditor from "simple-code-editor";
import "highlight.js/styles/github.css";
import {useRoute} from "vue-router";
import {dialog} from "@/dialog";
import SignalBus from "@/application/SignalBus";
import Router from "@/router";


const hooks = [
  {
    value: 'on_page_save',
    label: 'Wenn eine Seite gespeichert wird'
  }, {
    value: 'on_product_change',
    label: 'Wenn ein Wert in einem Produktfeld geändert wird'
  }, {
    value: 'on_product_export',
    label: 'Wenn ein InDesign exportiert wird'
  }
]

const route = useRoute()

const ruleId = ref(0);
const ruleTitle = ref('');
const ruleHook = ref([]);
const ruleScript = ref('');
const showError = ref(false);

function toggleHook(value) {
  const index = ruleHook.value.indexOf(value);
  if (index === -1) {
    ruleHook.value.push(value);
  } else {
    ruleHook.value.splice(index, 1);
  }
}

async function deleteRule() {
  dialog.deleteDialog({
    title: 'Regel löschen',
    message: 'Möchten Sie diese Regel wirklich löschen?',
    onAccept: async () => {
      const [data, err] = await mutation.deleteRule(ruleId.value);
      if (err) {
        console.error(err);
        return;
      }

      ruleId.value = -1;
      ruleTitle.value = '';
      ruleHook.value = [];
      ruleScript.value = 'log("Hello World")';

      SignalBus.emit('reload-administration-sidebar')
      Router.push('/administration/')
    }
  });
}

async function createRule() {
  if (!ruleTitle.value) {
    ruleTitle.value = 'Unnamed Rule';
  }

  let rule = {
    id: ruleId.value,
    title: ruleTitle.value,
    hook: ruleHook.value.join(',') || '',
    script: ruleScript.value
  }

  const [data, err] = await mutation.createOrUpdateRule(rule);
  showError.value = false;

  ruleId.value = data.id;

  if (err) {
    console.error(err);
    dialog.okDialog({
      title: 'Fehler',
      message: 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.'
    })
  } else {
    dialog.okDialog({
      title: 'Regel gespeichert',
      message: 'Die Regel wurde erfolgreich gespeichert.'
    })
  }

  SignalBus.emit('reload-administration-sidebar')
}

async function loadRule(id) {
  if (id === -1) {
    ruleId.value = -1;
    ruleTitle.value = '';
    ruleHook.value = [];
    ruleScript.value = 'log("Hello World")';
    return;
  }

  const [data, err] = await query.getRule(ruleId.value);
  if (err) {
    console.error(err);
    return;
  }

  ruleId.value = id;
  ruleTitle.value = data.title;
  ruleHook.value = data.hook.split(',');
  ruleScript.value = data.script;
}

watch(() => route.params.ruleId, (newVal) => {
  ruleId.value = newVal || -1;
  loadRule(ruleId.value);
});

onMounted(() => {
  ruleId.value = route.params.ruleId || -1;
  loadRule(ruleId.value);

  hljs.configure({
    languages: ['javascript', 'js']
  });
});
</script>